import React, {
    useContext, useEffect, useMemo, useRef, useState 
} from 'react';
import {
    Box, Slide, Typography, useTheme 
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import ProgressBar from '../../components/progressBar';
import {
    GOAL_CHOICES 
} from '../../config/types';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../../figmaTheme';

type Props = {
    position: number
};

export const GoalSteps = ({
    position
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const topGoalAnswer = useMemo(() => {
        return formContext?.answers['top_goal'] || 'health';
    }, [formContext?.answers]);
    const [step1Height, setStep1Height] = useState(null);
    const [step2Height, setStep2Height] = useState(null);
    const [step3Height, setStep3Height] = useState(null);
    const initialScreenWidth = useMemo(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth;
        }
        return 0;
    }, []);
    const [screenWidth, setScreenWidth] = useState(initialScreenWidth);
    const [skipAnimation, setSkipAnimation] = useState(false);
    const useStyles = useMemo(() => makeStyles((theme) => ({
        scrollableContainer: {
            overflow: 'scroll',
            scrollbarWidth: 'none',
            minHeight: '100%',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        },
        title: {
            marginBottom: '24px',
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards 0s`
        },
        steps: {
            width: '100%',
            lineHeight: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
        stepsContainer: {
            position: 'absolute',
            width: 'calc(100% - 48px)',
            display: 'flex',
            flexDirection: 'column',
            rowGap: '16px'
        },
        step: {
            backgroundColor: theme.palette.primary['75'],
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            columnGap: '12px',
            overflow: 'hidden',
        },
        curtain: {
            position: 'absolute',
            backgroundColor: theme.palette.white,
            zIndex: 1
        },
        curtain1Left: {
            width: '30px',
            animation: `$slideLeft ${skipAnimation ? 0 : 0.25}s forwards ${skipAnimation ? 0 : 0.75}s`
        },
        curtain1Right: {
            width: '100%',
            animation: `$slideRight ${skipAnimation ? 0 : 0.75}s forwards ${skipAnimation ? 0 : 0.75}s`
        },
        curtain2Left: {
            width: '30px',
            animation: `$slideLeft ${skipAnimation ? 0 : 0.25}s forwards ${skipAnimation ? 0 : 1.75}s`
        },
        curtain2Right: {
            width: '100%',
            animation: `$slideRight ${skipAnimation ? 0 : 0.75}s forwards ${skipAnimation ? 0 : 1.75}s`
        },
        curtain3Left: {
            width: '30px',
            animation: `$slideLeft ${skipAnimation ? 0 : 0.25}s forwards ${skipAnimation ? 0 : 2.75}s`
        },
        curtain3Right: {
            width: '100%',
            animation: `$slideRight ${skipAnimation ? 0 : 0.75}s forwards ${skipAnimation ? 0 : 2.75}s`
        },
        stepNumber: {
            width: '30px',
            display: 'flex',
            flexShrink: 0,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.white,
            fontFamily: 'Poppins',
            fontSize: '20px',
            lineHeight: '28px',
            fontWeight: TypographyWeights.BOLD
        },
        stepText: {
            padding: '12px 32px 12px 0',
            backgroundColor: theme.palette.primary['75'],
            margin: 0,
        },
        svg: {
            width: '100%'
        },
        lineA: {
            animation: `$snakeA ${skipAnimation ? 0 : 1}s linear forwards ${skipAnimation ? 0 : 1}s`
        },
        lineB: {
            animation: `$snakeB ${skipAnimation ? 0 : 1}s linear forwards ${skipAnimation ? 0 : 2}s`
        },
        lineC: {
            animation: `$snakeC ${skipAnimation ? 0 : 1}s linear forwards ${skipAnimation ? 0 : 3}s`
        },
        arc: {
            animation: `$snakeArc ${skipAnimation ? 0 : 1}s linear forwards ${skipAnimation ? 0 : 4}s`
        },
        circle: {
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 4.25}s`
        },
        icon: {
            width: '32px',
            height: '32px',
            fill: theme.palette.white,
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 4.25}s`
        },
        text: {
            textAlign: 'center',
            width: '70%',
            transform: 'translate(0, -24px)',
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 4.25}s`
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: '0' 
            },
            '100%': {
                opacity: '1' 
            },
        },
        '@keyframes fadeOut': {
            '0%': {
                opacity: '1' 
            },
            '100%': {
                opacity: '0' 
            },
        },
        '@keyframes slideLeft': {
            '0%': {
                width: '30px' 
            },
            '100%': {
                width: 0 
            },
        },
        '@keyframes slideRight': {
            '0%': {
                width: 'calc(100% - 48px)',
                left: `${Math.max(step1Height, step2Height, step3Height)}px` 
            },
            '100%': {
                width: 0,
                left: '100%' 
            },
        },
        '@keyframes snakeA': {
            '0%': {
                strokeDashoffset: `${(step1Height + step2Height) / 2 + 24}px` 
            },
            '100%': {
                strokeDashoffset: '0px' 
            },
        },
        '@keyframes snakeB': {
            '0%': {
                strokeDashoffset: `${(step2Height + step3Height) / 2 + 24}px` 
            },
            '100%': {
                strokeDashoffset: '0px' 
            },
        },
        '@keyframes snakeC': {
            '0%': {
                strokeDashoffset: `${step3Height + 8 + ((Math.min(screenWidth, 692) - 48) / 2) - Math.max(step1Height, step2Height, step3Height) / 2}px` 
            },
            '100%': {
                strokeDashoffset: '0px' 
            },
        },
        '@keyframes snakeArc': {
            '0%': {
                strokeDashoffset: '282px' 
            },
            '100%': {
                strokeDashoffset: '0px' 
            },
        }
    })), [step1Height, step2Height, step3Height, screenWidth, skipAnimation]);
    const classes = useStyles();
    const theme = useTheme();

    const GOAL_DATA = useMemo(() => ({
        health: `Resulting in <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.primary.main};">a happier, healthier you.</span>`,
        healthspan: `Allowing you to <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.primary.main};">age gracefully</span>, with lower impact on your health.`,
        weight: `Helping you <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.primary.main};">reach your weight and fitness goals.</span>`,
        sleep: `Resulting in <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.primary.main};">deeper, more restul sleep</span>`,
        hangover: `Resulting in <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.primary.main};">fewer next day effects,</span> and brighter, more productive days.`,
        blackout: `Resulting in <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.primary.main};">lower impact to memory,</span> and brighter, more productive days.`,
        money: `Resulting in less money spent on alcohol, and <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.primary.main};">more dollars in your pocket.</span>`,
        emotions: `Resulting in <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.primary.main};">less reliance on alcohol for coping,</span> and healthier strategies in its place.`,
        stress: `Resulting in <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.primary.main};">lower stress and anxiety,</span> and improved overall mental health.`,
        relationships: `Resulting in <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.primary.main};">healthier relationships with the people who matter to you.</span>`,
    }), [theme]);

    const onClick = () => {
        if (!skipAnimation) setSkipAnimation(true);
        else formContext.goNext();
    };

    const step1 = useRef(null);
    const step2 = useRef(null);
    const step3 = useRef(null);

    useEffect(() => {
        if (!step1.current) return;
        setStep1Height(step1.current.clientHeight < 64 ? 64 : step1.current.clientHeight);
    }, [step1]);

    useEffect(() => {
        if (!step2.current) return;
        setStep2Height(step2.current.clientHeight < 64 ? 64 : step2.current.clientHeight);
    }, [step2]);

    useEffect(() => {
        if (!step3.current) return;
        setStep3Height(step3.current.clientHeight < 64 ? 64 : step3.current.clientHeight);
    }, [step3]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const resizeListener = () => {
            setScreenWidth(window.innerHeight);
        };
        window.addEventListener('resize', resizeListener);

        setTimeout(() => {
            setSkipAnimation(true);
        }, 4500);

        return () => {
            window.removeEventListener('resize', resizeListener);
        };
    }, []);

    return (
        <CardContainer
            sectionKey={'goals'}
            goBack={formContext.goBack}
            goNext={onClick}
            hasGradient
        >
            <Card>
                <ProgressBar
                    currentPosition={position}
                    sectionKey={'goals'}
                />
                <Box className={classes.scrollableContainer}>
                    <Box className={classes.content}>
                        <Slide
                            in={true}
                            direction={formContext?.screenDirection}
                            timeout={500}
                            mountOnEnter
                            unmountOnExit
                        >
                            <Typography
                                variant={TypographyVariants.H3}
                                className={classes.title}
                            >
                                {'With Sunnyside, we\'ll work together to:'}
                            </Typography>
                        </Slide>
                        <Box className={classes.steps}>
                            <svg
                                className={classes.svg}
                                style={{
                                    height: `${(step1Height + step2Height + (3 * step3Height / 2)) + 113}px` 
                                }}
                            >
                                <path
                                    className={classes.lineA}
                                    d={`M ${Math.max(step1Height, step2Height, step3Height) / 2 - 4} ${step1Height / 2} l 0 ${(step1Height + step2Height) / 2 + 16}`}
                                    stroke={theme.palette.primary.main}
                                    strokeWidth="4px"
                                    strokeDashoffset={`${(step1Height + step2Height) / 2 + 24}px`}
                                    strokeDasharray={`${(step1Height + step2Height) / 2 + 24}px`}
                                />
                                <path
                                    className={classes.lineB}
                                    d={`M ${Math.max(step1Height, step2Height, step3Height) / 2 - 4} ${(step1Height + step2Height / 2) + 16} l 0 ${(step2Height + step3Height) / 2 + 16}`}
                                    stroke={theme.palette.primary.main}
                                    strokeWidth="4px"
                                    strokeDashoffset={`${(step2Height + step3Height) / 2 + 24}px`}
                                    strokeDasharray={`${(step2Height + step3Height) / 2 + 24}px`}
                                />
                                <path
                                    className={classes.lineC}
                                    d={`M ${Math.max(step1Height, step2Height, step3Height) / 2 - 4} ${(step1Height + step2Height + step3Height / 2) + 32} l 0 ${step3Height + 28} c 0 0 0 5 5 5 l ${((Math.min(screenWidth, 692) - 48) / 2) - Math.max(step1Height, step2Height, step3Height) / 2 - 49} 0`}
                                    fill="none"
                                    strokeDashoffset={`${step3Height + 8 + ((Math.min(screenWidth, 692) - 48) / 2) - Math.max(step1Height, step2Height, step3Height) / 2}px`}
                                    strokeDasharray={`${step3Height + 8 + ((Math.min(screenWidth, 692) - 48) / 2) - Math.max(step1Height, step2Height, step3Height) / 2}px`}
                                    stroke={theme.palette.primary.main}
                                    strokeLinecap={'round'}
                                    strokeWidth={'4px'}
                                />
                                <path
                                    className={classes.arc}
                                    d={`M ${((Math.min(screenWidth, 692) - 48) / 2) - 48} ${(step1Height + step2Height + (3 * step3Height / 2)) + 65} a 45 45 0 1 1 90 0`}
                                    fill="none"
                                    stroke={theme.palette.primary.main}
                                    strokeDashoffset={'282px'}
                                    strokeDasharray={'282px'}
                                    strokeLinecap={'round'}
                                    strokeWidth={'4px'}
                                />
                                <path
                                    className={classes.arc}
                                    d={`M ${((Math.min(screenWidth, 692) - 48) / 2) - 48} ${(step1Height + step2Height + (3 * step3Height / 2)) + 65} a 45 45 0 0 0 90 0`}
                                    fill="none"
                                    stroke={theme.palette.primary.main}
                                    strokeDashoffset={'282px'}
                                    strokeDasharray={'282px'}
                                    strokeLinecap={'round'}
                                    strokeWidth={'4px'}
                                />
                                <circle
                                    className={classes.circle}
                                    cx={((Math.min(screenWidth, 692) - 48) / 2) - 3}
                                    cy={(step1Height + step2Height + (3 * step3Height / 2)) + 65}
                                    r="29px"
                                    fill={theme.palette.primary.main}
                                />
                            </svg>
                            <Box
                                className={classes.icon}
                                style={{
                                    transform: `translate(-3px, -${2 + 45 + 16}px)` 
                                }}
                            >
                                {GOAL_CHOICES.find(choice => choice.key === topGoalAnswer)?.icon}
                            </Box>
                            <Box className={classes.stepsContainer}>
                                <Box>
                                    <Box
                                        className={`${classes.curtain} ${classes.curtain1Left}`}
                                        style={{
                                            height: `${step1Height}px` 
                                        }}
                                    />
                                    <Box
                                        className={`${classes.curtain} ${classes.curtain1Right}`}
                                        style={{
                                            height: `${step1Height}px` 
                                        }}
                                    />
                                    <Box
                                        className={classes.step}
                                        ref={step1}
                                        style={{
                                            height: step1Height,
                                            borderRadius: `${step1Height / 2}px 8px 8px ${step1Height / 2}px`,
                                            gridTemplateColumns: `${step1Height / 2 - 2}px 1fr` 
                                        }}
                                    >
                                        <Box
                                            className={classes.stepNumber}
                                            style={{
                                                height: step1Height,
                                                width: `${Math.max(step1Height, step2Height, step3Height) / 2 - 2}px`,
                                                borderRadius: `${step1Height / 2}px 0 0 ${step1Height / 2}px` 
                                            }}
                                        >
                                            1
                                        </Box>
                                        <Typography
                                            variant={TypographyVariants.H5}
                                            className={classes.stepText}
                                        >
                                            {'Reduce total drinks per week'}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box>
                                    <Box
                                        className={`${classes.curtain} ${classes.curtain2Left}`}
                                        style={{
                                            height: `${step2Height}px` 
                                        }}
                                    />
                                    <Box
                                        className={`${classes.curtain} ${classes.curtain2Right}`}
                                        style={{
                                            height: `${step2Height}px` 
                                        }}
                                    />
                                    <Box
                                        className={classes.step}
                                        ref={step2}
                                        style={{
                                            height: step2Height,
                                            borderRadius: `${step2Height / 2}px 8px 8px ${step2Height / 2}px`,
                                            gridTemplateColumns: `${step2Height / 2 - 2}px 1fr` 
                                        }}
                                    >
                                        <Box
                                            className={classes.stepNumber}
                                            style={{
                                                height: step2Height,
                                                width: `${Math.max(step1Height, step2Height, step3Height) / 2 - 2}px`,
                                                borderRadius: `${step2Height / 2}px 0 0 ${step2Height / 2}px` 
                                            }}
                                        >
                                            2
                                        </Box>
                                        <Typography
                                            variant={TypographyVariants.H5}
                                            className={classes.stepText}
                                        >
                                            {'Increase non-drinking days'}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box>
                                    <Box
                                        className={`${classes.curtain} ${classes.curtain3Left}`}
                                        style={{
                                            height: `${step3Height}px` 
                                        }}
                                    />
                                    <Box
                                        className={`${classes.curtain} ${classes.curtain3Right}`}
                                        style={{
                                            height: `${step3Height}px` 
                                        }}
                                    />
                                    <Box
                                        className={classes.step}
                                        ref={step3}
                                        style={{
                                            height: step3Height,
                                            borderRadius: `${step3Height / 2}px 8px 8px ${step3Height / 2}px`,
                                            gridTemplateColumns: `${step3Height / 2 - 2}px 1fr` 
                                        }}
                                    >
                                        <Box
                                            className={classes.stepNumber}
                                            style={{
                                                height: step3Height,
                                                width: `${Math.max(step1Height, step2Height, step3Height) / 2 - 2}px`,
                                                borderRadius: `${step3Height / 2}px 0 0 ${step3Height / 2}px` 
                                            }}
                                        >
                                            3
                                        </Box>
                                        <Typography
                                            variant={TypographyVariants.H5}
                                            className={classes.stepText}
                                        >
                                            {'Limit heavy drinking days'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                        <Typography
                            variant={TypographyVariants.H5}
                            className={classes.text}
                            dangerouslySetInnerHTML={{
                                __html: GOAL_DATA[topGoalAnswer] 
                            }}
                        />
                        <Box
                            style={{
                                height: '300px' 
                            }}
                        />
                    </Box>
                </Box>
            </Card>
        </CardContainer>
    );
};

export default GoalSteps;
